import './form-chat.css'

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Writer } from '../../../Components'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import {
  addRealNamesToPlaceholders,
  createNewProcess,
  getCorrectState,
  getLabelConversationCanceled,
  getLabelNoraLog
} from './Functions'
import { GigiChatMessages } from './GigiChatMessages'
import { Icon } from './Icons'

const language = cap.language.state || 'IT'
const t = translations[language]
const dateLocale = language === 'EN' ? 'en-GB' : 'it-IT'

const initials = (name = '') => {
  const cleaned = String(name).split('@')[0]
  const parts = cleaned.replace(/[._-]/g, ' ').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const formatStartedAt = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(dateLocale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const Banner = ({ tone, children }) => (
  <div className="gf-chat__banner" data-tone={tone}>
    {children}
  </div>
)

export const FormChat = ({ stateRef }) => {
  const { conversationId } = useParams()
  const { data: conversation = {}, isSuccess } = useCQuery(['conversation', conversationId])
  const [messages, setMessages] = useState([])

  useEffect(() => {
    if (
      (messages.length < conversation?.messages?.length ||
        messages.map((m) => m.attachments.length) <
          conversation?.messages?.map((m) => m.attachments.length)) &&
      isSuccess
    ) {
      const travellerEmail = conversation?.traveller?.email
      const isProduction = window?._env_?.NODE_ENV === 'production'
      setMessages(
        conversation?.messages
          ?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          .map(({ message, realMessageWithPPI, sender, createdAt, attachments }) => {
            let processedMessage = ''
            if (isProduction) {
              processedMessage = realMessageWithPPI || message || ''
            } else {
              processedMessage = addRealNamesToPlaceholders(
                message || '',
                realMessageWithPPI || '',
                stateRef
              )
            }
            return {
              message: processedMessage,
              user: sender === 'traveller' ? travellerEmail : 'Nora',
              createdAt: new Date(createdAt),
              attachments
            }
          })
      )
    }
  }, [conversation, isSuccess])

  const sendMessage = async (message) => {
    setMessages([...messages, { message, user: 'Nora', createdAt: new Date(), attachments: [] }])
    try {
      const newState = {
        ...getCorrectState(stateRef.current),
        operatorInfo: { messageToSend: message }
      }
      await createNewProcess(newState, conversationId, 'sendOperatorMessageFromForm')
      await FC.client.service('conversations').patch(conversationId, { status: 'open' })
      invalidateQuery(['conversation', 'conversations'])
    } catch (e) {
      showToast('error', t.errore, e?.message || t.gfErroreConfermaForm)
    }
  }

  if (!isSuccess) return null

  const travellerEmail = conversation?.traveller?.email || ''
  const isHandled = conversation?.status === 'handledByOperator'
  const isCanceled = conversation?.status === 'canceled'
  const noraExcluded = conversation?.noraIsIncluded === false

  return (
    <>
      <header className="gf-chat__head">
        <div className="gf-chat__title-row">
          <div className="gf-chat__title">
            <Icon name="sparkles" size={12} /> {t.gfConversazioneConNora}
          </div>
          {!isHandled && (
            <span className="gf-chat__readonly">
              <Icon name="lock" size={9} /> {t.gfSolaLettura}
            </span>
          )}
        </div>
        <div className="gf-chat__user">
          <span className="gf-chat__avatar">{initials(travellerEmail)}</span>
          <span className="gf-chat__email" title={travellerEmail}>
            {travellerEmail || '—'}
          </span>
        </div>
        <div className="gf-chat__meta">
          {conversation?.createdAt && (
            <span>
              {t.gfAvviata} · {formatStartedAt(conversation.createdAt)}
            </span>
          )}
          {conversationId && <span> · ID #{conversationId}</span>}
        </div>
        {!!conversation?.subjectMidoffice && (
          <div className="gf-chat__subject">{conversation.subjectMidoffice}</div>
        )}
      </header>

      {(noraExcluded || isCanceled) && (
        <div className="gf-chat__banners">
          {noraExcluded && (
            <Banner tone="danger">{getLabelNoraLog(stateRef?.current || {})}</Banner>
          )}
          {isCanceled && (
            <Banner tone="warning">{getLabelConversationCanceled(conversation)}</Banner>
          )}
        </div>
      )}

      <div className="gf-chat__body">
        <GigiChatMessages messages={messages} isReadOnly={!isHandled} />
      </div>

      {isHandled ? (
        <div className="gf-chat__writer">
          <Writer
            sendMessage={sendMessage}
            composerStyle={{ width: '100%', padding: 0, flex: 1, minWidth: 0 }}
            noAttachments
            noraDraft={stateRef.current.draftMessage}
            composerVariant="gigi"
            composerPlaceholder={t.gfChatComposerPlaceholder}
            composerSendAriaLabel={t.gfChatSend}
          />
        </div>
      ) : (
        <div className="gf-chat__foot">
          <Icon name="info" size={12} /> {t.gfPerModificareConversazione}
        </div>
      )}
    </>
  )
}
